import React, { useState } from 'react';
import { superAdminApi } from '../../services/api';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import toast from 'react-hot-toast';

// ── Reusable input ────────────────────────────────────────────────────────────
const Field = ({ label, type = 'text', value, onChange, placeholder, required }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      style={{
        width: '100%', padding: '11px 14px', border: '1.5px solid #e5e7eb',
        borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box',
        transition: 'border-color 0.2s',
      }}
      onFocus={e => e.target.style.borderColor = '#7c3aed'}
      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
    />
  </div>
);

// ── Card wrapper ──────────────────────────────────────────────────────────────
const Card = ({ icon, title, subtitle, children }) => (
  <div style={{
    background: '#fff', borderRadius: 16, border: '1px solid #ede9fe',
    overflow: 'hidden', marginBottom: 24,
    boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
  }}>
    <div style={{ padding: '20px 28px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
    <div style={{ padding: '24px 28px 28px' }}>
      {children}
    </div>
  </div>
);

// ── Submit button ─────────────────────────────────────────────────────────────
const SubmitBtn = ({ loading, label, loadingLabel }) => (
  <button
    type="submit"
    disabled={loading}
    style={{
      padding: '11px 28px',
      background: loading ? '#9ca3af' : 'linear-gradient(135deg, #7c3aed, #5b21b6)',
      color: '#fff', border: 'none', borderRadius: 10, fontSize: 14,
      fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
      transition: 'opacity 0.2s',
    }}
  >
    {loading ? loadingLabel : label}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
const SuperAdminSettings = () => {
  const { superAdmin } = useSuperAdminAuth();

  // Email change state
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Password change state
  const [passForm, setPassForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passLoading, setPassLoading] = useState(false);

  // ── Email change handler ───────────────────────────────────────────────────
  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.password) {
      return toast.error('Saare fields fill karo');
    }
    setEmailLoading(true);
    try {
      await superAdminApi.requestEmailChange(emailForm);
      setEmailSent(true);
      setEmailForm({ new_email: '', password: '' });
      toast.success(`Confirmation link ${emailForm.new_email} pe bhej diya gaya!`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Email change request failed');
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Password change handler ───────────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passForm.new_password !== passForm.confirm_password) {
      return toast.error('Naye passwords match nahi kar rahe');
    }
    if (passForm.new_password.length < 6) {
      return toast.error('Password kam se kam 6 characters ka hona chahiye');
    }
    setPassLoading(true);
    try {
      await superAdminApi.resetSuperAdminPassword({
        current_password: passForm.current_password,
        new_password: passForm.new_password,
      });
      setPassForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password successfully change ho gaya!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password change failed');
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>
        Account Settings
      </h1>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 28 }}>
        Super Admin account ki settings manage karo
      </p>

      {/* Current Info */}
      <div style={{
        background: '#f5f3ff', border: '1px solid #ede9fe', borderRadius: 12,
        padding: '16px 20px', marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{
          width: 44, height: 44, background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
          borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, color: '#fff', flexShrink: 0,
        }}>👑</div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{superAdmin?.name || 'Super Admin'}</div>
          <div style={{ fontSize: 13, color: '#7c3aed' }}>{superAdmin?.email}</div>
        </div>
      </div>

      {/* ── Email Change Card ─────────────────────────────────────────────── */}
      <Card icon="📧" title="Email Address Change" subtitle="Naye email pe confirmation link bheja jaayega">

        {emailSent ? (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
            padding: '16px 20px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📬</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 6 }}>
              Confirmation Email Bhej Diya!
            </div>
            <div style={{ fontSize: 13, color: '#15803d', marginBottom: 16 }}>
              Apna naya email inbox check karo aur confirmation link pe click karo.<br />
              Link 30 minute mein expire ho jaayega.
            </div>
            <button
              onClick={() => setEmailSent(false)}
              style={{
                padding: '8px 20px', background: '#fff', border: '1px solid #bbf7d0',
                borderRadius: 8, fontSize: 13, color: '#166534', cursor: 'pointer', fontWeight: 600,
              }}
            >
              Dobara Request Karo
            </button>
          </div>
        ) : (
          <form onSubmit={handleEmailChange}>
            <Field
              label="Naya Email Address"
              type="email"
              value={emailForm.new_email}
              onChange={e => setEmailForm(p => ({ ...p, new_email: e.target.value }))}
              placeholder="newemail@example.com"
              required
            />
            <Field
              label="Current Password (verify karne ke liye)"
              type="password"
              value={emailForm.password}
              onChange={e => setEmailForm(p => ({ ...p, password: e.target.value }))}
              placeholder="••••••••"
              required
            />
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#92400e',
            }}>
              ⚠️ Naye email pe ek confirmation link jaayega. Link click karne ke baad hi email change hoga.
            </div>
            <SubmitBtn loading={emailLoading} label="Confirmation Link Bhejo" loadingLabel="Bhej raha hai..." />
          </form>
        )}
      </Card>

      {/* ── Password Change Card ──────────────────────────────────────────── */}
      <Card icon="🔑" title="Password Change" subtitle="Apna current password deke naya set karo">
        <form onSubmit={handlePasswordChange}>
          <Field
            label="Current Password"
            type="password"
            value={passForm.current_password}
            onChange={e => setPassForm(p => ({ ...p, current_password: e.target.value }))}
            placeholder="••••••••"
            required
          />
          <Field
            label="Naya Password"
            type="password"
            value={passForm.new_password}
            onChange={e => setPassForm(p => ({ ...p, new_password: e.target.value }))}
            placeholder="Kam se kam 6 characters"
            required
          />
          <Field
            label="Naya Password Confirm Karo"
            type="password"
            value={passForm.confirm_password}
            onChange={e => setPassForm(p => ({ ...p, confirm_password: e.target.value }))}
            placeholder="••••••••"
            required
          />
          <SubmitBtn loading={passLoading} label="Password Change Karo" loadingLabel="Change ho raha hai..." />
        </form>
      </Card>
    </div>
  );
};

export default SuperAdminSettings;
